import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { invoicesAPI } from '../lib/api';
import { getCurrentCompanyId, getCurrentUserId } from '../utils/auth';
import { invoiceResponseData } from '../utils/invoiceResponse';

// Mock data for fallback
const mockInvoices = [
  {
    id: '1',
    invoiceNumber: 'INV-2026-001',
    customerName: 'Tech Solutions Inc.',
    customerEmail: 'john@techsolutions.com',
    amount: 5500.00,
    paidAmount: 0.00,
    issueDate: '2026-01-15',
    dueDate: '2026-02-14',
    status: 'Sent'
  },
  {
    id: '2',
    invoiceNumber: 'INV-2026-002',
    customerName: 'Marketing Masters LLC',
    customerEmail: 'sarah@marketingmasters.com',
    amount: 3200.00,
    paidAmount: 3200.00,
    issueDate: '2026-01-10',
    dueDate: '2026-01-25',
    status: 'Paid'
  },
  {
    id: '3',
    invoiceNumber: 'INV-2026-003',
    customerName: 'Retail Plus Corp',
    customerEmail: 'mike@retailplus.com',
    amount: 8750.00,
    paidAmount: 0.00,
    issueDate: '2026-01-20',
    dueDate: '2026-03-06',
    status: 'Overdue'
  }
];

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const normalizeInvoice = (invoice) => {
  const rawLineItems = Array.isArray(invoice?.Items)
    ? invoice.Items
    : Array.isArray(invoice?.items)
      ? invoice.items
      : Array.isArray(invoice?.lineItems)
        ? invoice.lineItems
        : [];

  const lineItems = rawLineItems.map((item) => ({
    id: item?.Id ?? item?.id,
    invoiceId: item?.InvoiceId ?? item?.invoiceId,
    description: item?.Description ?? item?.description ?? '',
    quantity: toNumber(item?.Quantity ?? item?.quantity, 1),
    rate: toNumber(item?.UnitPrice ?? item?.unitPrice ?? item?.rate),
    amount: toNumber(item?.Amount ?? item?.amount),
    lineNumber: item?.LineNumber ?? item?.lineNumber
  }));

  const amount = toNumber(invoice?.Amount ?? invoice?.amount);
  const taxAmount = toNumber(invoice?.TaxAmount ?? invoice?.taxAmount);
  const explicitSubtotal = invoice?.Subtotal ?? invoice?.subtotal;
  const subtotalFromItems = lineItems.reduce((sum, item) => sum + item.amount, 0);
  const subtotal = explicitSubtotal != null
    ? toNumber(explicitSubtotal)
    : subtotalFromItems > 0
      ? subtotalFromItems
      : Math.max(amount - taxAmount, 0);
  const explicitVatRate = invoice?.VatRate ?? invoice?.vatRate;
  const vatRate = explicitVatRate != null
    ? toNumber(explicitVatRate)
    : subtotal > 0 && taxAmount > 0
      ? Number(((taxAmount / subtotal) * 100).toFixed(2))
      : 0;

  return {
    id: invoice?.Id ?? invoice?.id,
    companyId: invoice?.CompanyId ?? invoice?.companyId,
    customerId: invoice?.CustomerId ?? invoice?.customerId,
    invoiceNumber: invoice?.InvoiceNumber ?? invoice?.invoiceNumber,
    documentType: invoice?.DocumentType ?? invoice?.documentType ?? 'Invoice',
    customerName: invoice?.CustomerName ?? invoice?.customerName,
    customerEmail: invoice?.CustomerEmail ?? invoice?.customerEmail,
    amount,
    paidAmount: toNumber(invoice?.PaidAmount ?? invoice?.paidAmount),
    taxAmount,
    discountAmount: toNumber(invoice?.DiscountAmount ?? invoice?.discountAmount),
    issueDate: invoice?.IssueDate ?? invoice?.issueDate,
    dueDate: invoice?.DueDate ?? invoice?.dueDate,
    status: invoice?.Status ?? invoice?.status,
    notes: invoice?.Notes ?? invoice?.notes,
    terms: invoice?.Terms ?? invoice?.terms,
    createdAt: invoice?.CreatedAt ?? invoice?.createdAt,
    updatedAt: invoice?.UpdatedAt ?? invoice?.updatedAt,
    lineItems,
    vatRate,
    subtotal,
    currency: invoice?.Currency ?? invoice?.currency ?? 'KES',
    exchangeRate: toNumber(invoice?.ExchangeRate ?? invoice?.exchangeRate, 1)
  };
};

export const useInvoices = (companyId) => {
  return useQuery({
    queryKey: ['invoices', companyId],
    queryFn: async () => {
      console.log('useInvoices - Starting fetch with companyId:', companyId);
      
      if (!companyId) {
        console.warn('useInvoices - No company ID provided, returning empty array');
        return [];
      }

      try {
        console.log('useInvoices - Making API call to:', `/api/Invoices/company/${companyId}`);
        const response = await invoicesAPI.getAll(companyId);
        console.log('useInvoices - Raw API response:', response);
        console.log('useInvoices - Response data:', response.data);
        console.log('useInvoices - Response status:', response.status);
        
        // Handle the specific API response format: { Success: true, Data: [...] }
        const invoices = invoiceResponseData(response);
        if (!Array.isArray(invoices)) throw new Error('The server returned an invalid document list.');
        
        console.log('useInvoices - Final processed invoices:', invoices);
        console.log('useInvoices - Invoices count:', invoices.length);
        
        // Map API fields if needed (handle both PascalCase and camelCase)
        const mappedInvoices = invoices.map(normalizeInvoice);
        
        console.log('useInvoices - Mapped invoices:', mappedInvoices);
        return mappedInvoices;
      } catch (error) {
        console.error('useInvoices - API error:', error);
        console.error('useInvoices - Error details:', {
          message: error.message,
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          config: error.config
        });
        
        // Optionally return mock data for development/testing
        const useMockData = import.meta.env.VITE_USE_MOCK_DATA === 'true';
        if (useMockData) {
          console.log('useInvoices - Using mock data due to VITE_USE_MOCK_DATA=true');
          return mockInvoices;
        }
        
        throw error;
      }
    },
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnMount: 'always',
    cacheTime: 10 * 60 * 1000, // 10 minutes
    retry: (failureCount, error) => {
      console.log(`useInvoices - Retry attempt ${failureCount} for error:`, error.message);
      // Don't retry on 4xx errors (client errors)
      if (error?.response?.status >= 400 && error?.response?.status < 500) {
        return false;
      }
      // Retry up to 2 times for other errors
      return failureCount < 2;
    },
  });
};

export const useInvoice = (id) => {
  return useQuery({
    queryKey: ['invoice', id],
    queryFn: async () => {
      try {
        const response = await invoicesAPI.getById(id);
        const rawInvoice = invoiceResponseData(response);
        return rawInvoice ? normalizeInvoice(rawInvoice) : null;
      } catch (error) {
        throw error;
      }
    },
    enabled: !!id,
  });
};

export const useCreateInvoice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (invoiceData) => {
      try {
        const companyId = getCurrentCompanyId();
        const userId = getCurrentUserId();
        const documentType = invoiceData.documentType || 'Invoice';
        const invoicePrefix = documentType === 'Quote' ? 'QUO' : documentType === 'Proforma' ? 'PRO' : 'INV';
        const apiData = {
          companyId: companyId,
          customerId: invoiceData.isNewCustomer ? null : invoiceData.customer,
          invoiceNumber: `${invoicePrefix}-${new Date().getFullYear()}-${String(Date.now()).slice(-6)}`,
          documentType,
          customerName: invoiceData.customerName || '',
          customerEmail: invoiceData.customerEmail || '',
          amount: invoiceData.total,
          paidAmount: 0,
          taxAmount: invoiceData.vat,
          discountAmount: 0,
          issueDate: new Date(invoiceData.issueDate).toISOString(),
          dueDate: new Date(invoiceData.dueDate).toISOString(),
          status: 'Draft',
          notes: invoiceData.notes || '',
          terms: invoiceData.terms || '',
          revenueAccountId: invoiceData.revenueAccountId || null,
          userId: userId,
          lineItems: invoiceData.lineItems,
          vatRate: invoiceData.vatRate,
          subtotal: invoiceData.subtotal,
          currency: invoiceData.currency || 'KES',
          exchangeRate: invoiceData.exchangeRate || 1
        };

        console.log('Creating invoice with data:', apiData);
        console.log('Using endpoint: POST /api/Invoices');
        const response = await invoicesAPI.create(apiData);
        console.log('Invoice creation response:', response);
        
        // Handle the API response format: { Success: true, Data: {...} }
        const saved = invoiceResponseData(response);
        if (typeof saved === 'string' && saved) return { id: saved };
        if (saved?.Id || saved?.id) return normalizeInvoice(saved);
        
        throw new Error('Invalid API response format');
      } catch (error) {
        console.error('Error creating invoice:', error);
        throw error;
      }
    },
    onSuccess: (data, variables) => {
      // Invalidate invoices list to refresh the data
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      
      // Only cache complete documents; creation can return just an ID.
      if ((data.id || data.Id) && data.invoiceNumber) {
        queryClient.setQueryData(['invoice', data.id || data.Id], data);
      }
      
      console.log('Invoice created successfully:', data);
    },
    onError: (error) => {
      console.error('Invoice creation failed:', error);
    }
  });
};

export const useUpdateInvoice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }) => {
      try {
        const apiData = {
          id,
          customerId: data.isNewCustomer ? null : data.customer,
          customerName: data.customerName || '',
          customerEmail: data.customerEmail || '',
          amount: data.total,
          paidAmount: data.paidAmount || 0,
          taxAmount: data.vat,
          discountAmount: 0,
          issueDate: new Date(data.issueDate).toISOString(),
          dueDate: new Date(data.dueDate).toISOString(),
          status: data.status || 'Draft',
          notes: data.notes || '',
          terms: data.terms || '',
          currency: data.currency || 'KES',
          exchangeRate: data.exchangeRate || 1,
          lineItems: data.lineItems
        };
        console.log('Updating invoice with data:', { id, data: apiData });
        const response = await invoicesAPI.update(id, apiData);
        console.log('Invoice update response:', response);
        
        // Handle the API response format: { Success: true, Data: {...} }
        if (response.data && response.data.Success) {
          return response.data.Data;
        } else if (response.data) {
          return response.data;
        }
        
        throw new Error('Invalid API response format');
      } catch (error) {
        console.error('Error updating invoice:', error);
        throw error;
      }
    },
    onSuccess: (data, variables) => {
      // Update the invoice in cache
      queryClient.setQueryData(['invoice', variables.id], data);
      
      // Invalidate invoices list
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      
      console.log('Invoice updated successfully:', data);
    },
    onError: (error) => {
      console.error('Invoice update failed:', error);
    }
  });
};

export const useDeleteInvoice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await invoicesAPI.delete(id);
      // Check for API-level failure even on 2xx HTTP status
      if (response.data && response.data.Success === false) {
        throw new Error(response.data.Message || 'Failed to delete invoice');
      }
      return { success: true };
    },
    onSuccess: (data, id) => {
      // Remove invoice from cache
      queryClient.removeQueries({ queryKey: ['invoice', id] });
      
      // Invalidate invoices list
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
};

export const useApproveInvoice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      try {
        console.log('Approving invoice:', id);
        const response = await invoicesAPI.approve(id);
        console.log('Invoice approval response:', response);
        
        // Handle the API response format: { Success: true, Data: {...} }
        if (response.data && response.data.Success) {
          return response.data.Data;
        } else if (response.data) {
          return response.data;
        }
        
        throw new Error('Invalid API response format');
      } catch (error) {
        console.error('Error approving invoice:', error);
        throw error;
      }
    },
    onSuccess: (data, id) => {
      // Update the invoice in cache
      if (data) {
        queryClient.setQueryData(['invoice', id], data);
      }
      
      // Invalidate invoices list to refresh the data
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      
      console.log('Invoice approved successfully:', data);
    },
    onError: (error) => {
      console.error('Invoice approval failed:', error);
    }
  });
};
